import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useUser } from './LoginPersistence';
import { getResponsiveValue } from '../styles/breakpoints';

const AuthInput = ({ label, value, onChangeText, placeholder, onSubmitEditing, error }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <View style={styles.inputRow}>
      <Text 
        style={[
          styles.authInputLabel, 
          { 
            fontSize: label.length > 12 
              ? getResponsiveValue({
                  mobile: 16,
                  tablet: 18,
                  laptop: 20,
                  desktop: 20,
                })
              : getResponsiveValue({
                  mobile: 20,
                  tablet: 22,
                  laptop: 24,
                  desktop: 24,
                }), 
          }
        ]}
      >
        {label}
      </Text>
      <TextInput 
        onBlur={() => setIsFocused(false)} 
        onFocus={() => setIsFocused(true)} 
        style={[
          styles.authInput,
          error && styles.authInputError
        ]} 
        value={value} 
        onChangeText={onChangeText} 
        onSubmitEditing={onSubmitEditing}
        placeholder={placeholder} 
        placeholderTextColor={!isFocused ? '#8f8c8a' : 'transparent'} 
        returnKeyType="done"
        autoCapitalize="characters"
      />
    </View>
  );
};

export const CommunityMemberUpgradeForm = ({ 
  message = "You must be a community member to access this feature.",
  onSuccess = () => {},
  onError = () => {},
  containerStyle = {},
  isModal = false,
  onClose = () => {},
}) => {
  const auth = getAuth();
  const { user, setUser, validateInviteCode, claimInviteCode } = useUser();
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleFieldChange = (value) => {
    setInviteCode(value.toUpperCase());
    setError(''); // Clear error when user types
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      if (!inviteCode || !inviteCode.trim()) {
        setError('Please enter an invite code');
        setLoading(false);
        return;
      }

      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('You must be logged in to upgrade to community member');
        setLoading(false);
        return;
      }

      // Check if user is already a community member
      if (user?.isCommunityMember) {
        setError('You are already a community member');
        setLoading(false);
        return;
      }

      // Validate invite code
      const codeValidation = await validateInviteCode(inviteCode.trim().toUpperCase());
      if (!codeValidation.valid) {
        setError(codeValidation.error);
        setLoading(false);
        return;
      }

      const codeId = codeValidation.codeId;

      // Update user to be a community member
      const db = getFirestore();
      const userRef = doc(db, 'users', currentUser.uid);
      
      await updateDoc(userRef, {
        isCommunityMember: true
      });

      // Claim the invite code
      const claimResult = await claimInviteCode(codeId, currentUser.uid);
      if (!claimResult.success) {
        console.error('Failed to claim invite code:', claimResult.error);
        // Don't fail the upgrade if claiming fails, but log it
      }

      // Update local user state
      const updatedUser = { ...user, isCommunityMember: true };
      setUser(updatedUser);

      // Clear form
      setInviteCode('');
      setError('');

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error upgrading to community member:', err);
      const errorMessage = err.message || 'Failed to upgrade to community member. Please try again.';
      setError(errorMessage);
      if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={styles.formContainer}>
        {isModal && (
          <TouchableOpacity style={styles.closeButton} onPress={() => onClose()}>
            <Text style={styles.closeButtonText}>x</Text>
          </TouchableOpacity>
        )}
        <View style={styles.messageContainer}>
            <Text style={styles.messageText}>
            {message}
            </Text>
        </View>
        <AuthInput
          label="Invite Code"
          value={inviteCode}
          placeholder="Enter Invite Code"
          onChangeText={handleFieldChange}
          onSubmitEditing={handleSubmit}
          error={!!error}
        />

        <View style={styles.submitButtonWrapper}>
          <TouchableOpacity 
            style={[
              styles.submitButtonContainer,
              loading && styles.disabledButton
            ]} 
            onPress={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <ActivityIndicator size="small" color="#1075ae" />
            ) : (
              <Text style={styles.submitButtonText}>SUBMIT</Text>
            )}
          </TouchableOpacity>
        </View>

        {/* Error Display */}
        {error && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  messageContainer: {
    marginBottom: getResponsiveValue({
      mobile: 30,
      tablet: 35,
      laptop: 45,
      desktop: 50
    }),
  },
  formContainer: {
    width: '100%',
    maxWidth: getResponsiveValue({
      mobile: '100%',
      tablet: 400,
      laptop: 500,
      desktop: 500
    }),
    backgroundColor: '#feffe4',
    borderRadius: getResponsiveValue({
      mobile: 20,
      tablet: 25,
      laptop: 30,
      desktop: 35
    }),
    paddingVertical: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 25,
      desktop: 30
    }),
    paddingHorizontal: getResponsiveValue({
      mobile: 25,
      tablet: 30,
      laptop: 35,
      desktop: 40
    }),
    position: 'relative',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    }),
  },
  closeButton: {
    position: 'absolute',
    top: 5,
    right: 5,
    padding: 10,
    borderRadius: 15,
    zIndex: 10,
  },
  closeButtonText: {
    color: '#8f8c8a',
    fontSize: getResponsiveValue({
      mobile: 20,
      tablet: 22,
      laptop: 24,
      desktop: 24
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
    gap: 15,
    width: '100%',
    cursor: 'text',
  },
  authInputLabel: {
    color: '#8f8c8a',
    fontFamily: 'VastagoGrotesk',
    fontWeight: '400',
    whiteSpace: 'nowrap',
    paddingTop: getResponsiveValue({
      mobile: 10,
      tablet: 10,
      laptop: 4,
      desktop: 4
    }),
  },
  authInput: {
    borderBottomWidth: 2,
    borderBottomColor: '#8f8c8a',
    borderStyle: 'solid',
    width: '100%',
    color: '#8f8c8a',
    fontFamily: 'Computer Says No',
    fontWeight: '400',
    paddingLeft: 8,
    fontSize: Platform.OS === 'web' ? 28 : 22,
    ...Platform.select({
      web: {
        outlineStyle: 'none',
      },
    }),
  },
  authInputError: {
    borderBottomColor: '#e74c3c',
  },
  submitButtonWrapper: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 15,
  },
  submitButtonContainer: {
    backgroundColor: '#dfffdd',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 5,
    alignItems: 'center',
    justifyContent: 'center',
    width: 'auto',
    minWidth: 200,
  },
  submitButtonText: {
    color: '#1075ae',
    fontFamily: 'Playpen Sans',
    fontSize: getResponsiveValue({
      mobile: 14,
      tablet: 15,
      laptop: 16,
      desktop: 16
    }),
    fontWeight: '700',
  },
  disabledButton: {
    opacity: 0.6,
  },
  errorContainer: {
    marginTop: 15,
    padding: 12,
    backgroundColor: 'rgba(231, 76, 60, 0.1)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(231, 76, 60, 0.3)',
  },
  errorText: {
    color: '#e74c3c',
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    textAlign: 'center',
  },
  messageText: {
    fontFamily: 'Playpen Sans',
    fontWeight: '500',
    textAlign: 'center',
    fontSize: getResponsiveValue({
      mobile: 14,
      tablet: 14,
      laptop: 18,
      desktop: 18
    }),
    color: '#578dff',
  },
});

export default CommunityMemberUpgradeForm;

